import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { LinkButton } from '../../CommonComponents/Button';
import FormikInput from '../../CommonComponents/FormikInput';
import FormikMaskedInput from '../../CommonComponents/FormikMaskedInput';
import FormikReactSelect from '../../CommonComponents/FormikReactSelect';
import Input from '../../CommonComponents/Input';
import LoadingMask from '../../CommonComponents/LoadingMask';
import { dateMask } from '../../CommonComponents/MaskedInput';
import { currentClientSelector } from '../../CommonSelectors/CommonAreaSelector';
import { PulseCheckCategoryIcon } from './PulseCheckCategoryIcon';
import { PulseCheckCategory } from './ActionStepsV2.meta';

const Row = styled.div`
  display: flex;
  align-items: center;

  & + & {
    margin-top: 16px;
  }

  ${(Input as any).StyledInput} {
    width: 100%;
  }
`;

const IconColumn = styled.div`
  width: 50px;
  padding-right: 8px;
  flex-shrink: 0;
`;

const PulseCheckIconColumn = styled(IconColumn)`
  width: 28px;
`;

const InputsColumn = styled.div`
  flex-grow: 1;
  display: flex;
  padding-right: 8px;

  > * + * {
    padding-left: 16px;
  }
`;

const ActionsColumn = styled.div`
  width: 110px;
  flex-shrink: 0;
`;

const DoneButton = styled(LinkButton)`
  color: #0078d4;
`;

const ACTION_STEP_ICON_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];

enum AssigneeType {
  Primary,
  Partner,
  Adviser,
}

interface AssigneeOption {
  label: string;
  value: number | null;
  type: AssigneeType;
}

interface Props {
  onDone: (values: any) => void;
  onCancel: () => void;
  advisersInTheSameOrg: any[];
  isLoadingAdvisersInTheSameOrg: boolean;
  item: any;
  isAssignable: boolean;
  placeholder: string;
  isAdviserArea: boolean;
  pulseCheckCategory?: PulseCheckCategory;
}

export const ActionStepItem: React.FC<Props> = ({
  onDone,
  onCancel,
  advisersInTheSameOrg,
  isLoadingAdvisersInTheSameOrg,
  item,
  isAssignable,
  placeholder,
  isAdviserArea,
  pulseCheckCategory,
}) => {
  const currentClient = useSelector(currentClientSelector);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const assigneeOptions = useMemo(() => {
    const assignees: AssigneeOption[] = [];

    if (currentClient) {
      assignees.push({
        label: `${currentClient.FirstName} ${currentClient.LastName}`,
        value: null,
        type: AssigneeType.Primary,
      });

      if (currentClient.PartnerName) {
        assignees.push({
          label: `${currentClient.PartnerName}`,
          value: null,
          type: AssigneeType.Partner,
        });
      }
    }

    if (advisersInTheSameOrg) {
      advisersInTheSameOrg.map(({ AdviserId, FirstName, LastName }) =>
        assignees.push({
          value: AdviserId,
          label: `${FirstName} ${LastName}`,
          type: AssigneeType.Adviser,
        }),
      );
    }

    return assignees.map((assignee, index) => ({
      ...assignee,
      assignToId: index,
    }));
  }, [currentClient, advisersInTheSameOrg]);

  const assignToInitialValue = assigneeOptions.find(
    ({ value, type }) => value === item.AssigneeId && type === item.AssigneeType,
  );

  return (
    <Formik
      initialValues={{
        ...item,
        DueDate: item.DueDate ? moment(item.DueDate).format('MM/DD/YYYY') : '',
        AssignTo: assignToInitialValue
          ? { value: assignToInitialValue.assignToId, label: assignToInitialValue.label }
          : null,
      }}
      onSubmit={(values, { resetForm }) => {
        onDone(values);
        resetForm();
      }}
      render={({ values, setFieldValue }) => {
        return (
          <Form>
            <Row>
              {typeof pulseCheckCategory === 'number' ? (
                <PulseCheckIconColumn>
                  <PulseCheckCategoryIcon category={pulseCheckCategory} />
                </PulseCheckIconColumn>
              ) : (
                <IconColumn>
                  <div
                    className="action-steps-icon-wrapper"
                    tabIndex={0}
                    role="button"
                    aria-pressed="false"
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                    onKeyPress={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <i className="action-steps-icon" data-type={values.Icon} />
                    {!(isAssignable && values.IsGenerated) && <i className="fa fa-caret-down" />}

                    {isDropdownOpen && !(isAssignable && values.IsGenerated) && (
                      <div className="action-steps-icon-wrapper menu-open">
                        <ul className="adv-dropdown">
                          {ACTION_STEP_ICON_OPTIONS.map(option => (
                            <li
                              key={option}
                              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                              role="button"
                              aria-pressed="false"
                              tabIndex={0}
                              onClick={() => {
                                setFieldValue('Icon', option);
                                setIsDropdownOpen(false);
                              }}
                              onKeyPress={() => {
                                setFieldValue('Icon', option);
                                setIsDropdownOpen(false);
                              }}
                            >
                              <i className="action-steps-icon" data-type={option} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </IconColumn>
              )}

              <InputsColumn>
                <Field
                  component={FormikInput}
                  name="Text"
                  placeholder={placeholder}
                  disabled={isAssignable && values.IsGenerated}
                />
              </InputsColumn>

              {isAssignable && isAdviserArea ? (
                <ActionsColumn />
              ) : (
                <ActionsColumn>
                  <DoneButton type="submit">Done</DoneButton>
                  <LinkButton onClick={onCancel}>Cancel</LinkButton>
                </ActionsColumn>
              )}
            </Row>

            {isAssignable && isAdviserArea && (
              <Row>
                {typeof pulseCheckCategory === 'number' ? <PulseCheckIconColumn /> : <IconColumn />}

                <InputsColumn>
                  {isLoadingAdvisersInTheSameOrg ? (
                    <LoadingMask isDark height="50px" />
                  ) : (
                    <Field
                      name="AssignTo"
                      component={FormikReactSelect}
                      placeholder="Assign To"
                      options={assigneeOptions.map(({ assignToId, label }) => ({
                        value: assignToId,
                        label,
                      }))}
                      onChange={selectedValue => {
                        const opt = assigneeOptions.find(
                          ({ assignToId }) => selectedValue.value === assignToId,
                        );

                        if (opt) {
                          setFieldValue('AssignTo', selectedValue);
                          setFieldValue('AssigneeId', opt.value);
                          setFieldValue('AssigneeType', opt.type);
                        }
                      }}
                    />
                  )}

                  <Field
                    component={FormikMaskedInput}
                    name="DueDate"
                    mask={dateMask}
                    guide={false}
                    placeholder="MM/DD/YYYY (Due Date)"
                  />
                </InputsColumn>

                <ActionsColumn>
                  <DoneButton type="submit">Done</DoneButton>
                  <LinkButton onClick={onCancel}>Cancel</LinkButton>
                </ActionsColumn>
              </Row>
            )}
          </Form>
        );
      }}
    />
  );
};
